import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Skeleton,
  Tooltip,
  Box,
  TextField,
  InputAdornment,
  Typography,
  Avatar,
  SxProps,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { sendApiRequest, readFile } from 'lib/api/public';
import notify from 'lib/notify';
import { Candidate } from 'components/candidate/types';
import { Client } from 'components/client/types';
import { Job } from 'components/job/types';
import Uploader from './common/Uploader';
import dynamic from 'next/dynamic';
import Link from 'components/common/Link';
import DataGrid, { Item } from './common/DataGrid';
import { type OpenRecordConfig } from './common/datagrid/RecordOpenCell';
import Modal from './common/Modal';
import { CustomTabPanel } from 'components/utils';
import Group from 'components/common/Group';
import { commonRightModalProps } from './utils';

const CandidatePreview = dynamic(() => import('components/candidate/CandidatePreview'), { ssr: false });
const JobPreview = dynamic(() => import('components/job/JobPreview'), { ssr: false });
const ClientPreview = dynamic(() => import('components/client/ClientPreview'), { ssr: false });

const CANDIDATES_TAB = 0;
const CLIENTS_TAB = 1;
const JOBS_TAB = 2;

type CandidateInputMode = 'query' | 'resume' | 'job';

const globalSearchTabPanelSx = { marginTop: '10px', padding: 0 };

const GLOBAL_SEARCH_MIN_QUERY_LENGTH = 3;

const GLOBAL_SEARCH_INPUT_PLACEHOLDER = `Type at least ${GLOBAL_SEARCH_MIN_QUERY_LENGTH} characters and press Enter to search.`;

const JOB_DESCRIPTION_PANEL_HEIGHT = 250;

// OpenSearch stops counting hits at 10,000 by default.
const SEARCH_TOTAL_COUNT_CAP = 10000;

const formatTotalCount = (count: number) =>
  count >= SEARCH_TOTAL_COUNT_CAP ? `${SEARCH_TOTAL_COUNT_CAP.toLocaleString()}+` : count.toLocaleString();

const renderCandidateSearchBusyAlert = (title: string, message: string) => (
  <Alert
    severity="info"
    icon={<CircularProgress size={22} color="inherit" />}
    sx={{ mb: 2, alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%', pt: 0.25 } }}
  >
    <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
      {message}
    </Typography>
    <LinearProgress color="secondary" sx={{ mt: 1.5 }} />
  </Alert>
);

const renderBooleanSearchInfo = () => (
  <Alert severity="info" sx={{ mb: 1.5 }}>
    Search resume text with AND (both words must appear) or OR (either word is fine). Example: clinical trials AND
    antibody.
  </Alert>
);

type WhyMatch = {
  summary: string;
  reasons: { field: string; label: string; priority: number; detail?: string }[];
};

type MatchedContact = {
  _id: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  primaryEmail?: string;
};

type CandidateSearchResult = Candidate & { whyMatch?: WhyMatch };
type ClientSearchResult = Client & {
  whyMatch?: WhyMatch;
  matchType?: 'direct' | 'contact';
  matchedContact?: MatchedContact;
};
type JobSearchResult = Job & { whyMatch?: WhyMatch };

type CandidateRecentJobSummary = {
  _id: string;
  title: string;
};

type CandidateJobSummary = {
  recentJob: CandidateRecentJobSummary | null;
};

const renderCandidateJobCellPlaceholder = () => <Skeleton variant="text" width="80%" height={20} />;

const GlobalSearchCandidateAllJobs = ({ candidate }: { candidate: CandidateSearchResult }) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const jobsCount = candidate.jobIds?.length ?? 0;
  const candidateName = [candidate.firstName, candidate.lastName].filter(Boolean).join(' ');

  useEffect(() => {
    if (!open) {
      return;
    }

    setLoading(true);

    void sendApiRequest({
      path: `/candidates/jobs?candidateId=${candidate._id}`,
      method: 'GET',
    })
      .then(response => {
        setJobs(response as Job[]);
      })
      .catch(error => {
        notify(error);
        setJobs([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [candidate._id, open]);

  return (
    <>
      <Chip
        label={jobsCount}
        size="small"
        onClick={() => jobsCount > 0 && setOpen(true)}
        sx={{ cursor: jobsCount > 0 ? 'pointer' : 'default', minWidth: '32px' }}
      />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`${candidateName} — Jobs`}
        renderContent={() => (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress color="secondary" size={24} />
              </Box>
            ) : jobs.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                No jobs found
              </Typography>
            ) : (
              jobs.map(job => (
                <Link key={job._id} href={`/job?id=${job._id}`} target="_blank" sx={{ cursor: 'pointer' }}>
                  {job.title}
                </Link>
              ))
            )}
          </Box>
        )}
      />
    </>
  );
};

const globalSearchInputSx: SxProps = {
  width: {
    xs: '100%',
    sm: '100%',
    md: '100%',
    lg: '100%',
    xl: '100%',
  },
  marginBottom: {
    xs: 1,
    sm: 0,
  },
};

const globalSearchResultsTableSx: SxProps = {
  width: '100%',
  '& .MuiTable-root': {
    width: '100%',
  },
};

const GlobalSearchTabInput = ({
  value: valueProp,
  onSearch,
  onClear,
  loading = false,
}: {
  value: string;
  onSearch: (value: string) => void;
  onClear: () => void;
  loading?: boolean;
}) => {
  const [value, setValue] = useState(valueProp);
  const [showClearIcon, setShowClearIcon] = useState(valueProp ? 'flex' : 'none');
  const [hintError, setHintError] = useState(false);

  useEffect(() => {
    setValue(valueProp);
    setShowClearIcon(valueProp ? 'flex' : 'none');
  }, [valueProp]);

  const handleClear = () => {
    setValue('');
    setShowClearIcon('none');
    setHintError(false);
    onClear();
  };

  const handleSearchChange = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const nextValue = (e.target as HTMLInputElement).value.trim();
      setShowClearIcon(nextValue ? 'flex' : 'none');

      if (nextValue.length > 0 && nextValue.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) {
        setHintError(true);
        return;
      }

      setHintError(false);
      onSearch(nextValue);
    }
  };

  return (
    <TextField
      size="small"
      variant="outlined"
      onChange={e => {
        const nextValue = e.target.value;
        setValue(nextValue);

        if (hintError && (nextValue.trim().length === 0 || nextValue.trim().length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH)) {
          setHintError(false);
        }
      }}
      value={value}
      onKeyDown={handleSearchChange}
      placeholder={GLOBAL_SEARCH_INPUT_PLACEHOLDER}
      helperText={hintError ? `Enter at least ${GLOBAL_SEARCH_MIN_QUERY_LENGTH} characters to search.` : undefined}
      error={hintError}
      sx={globalSearchInputSx}
      inputProps={{ 'aria-label': GLOBAL_SEARCH_INPUT_PLACEHOLDER }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end" style={{ display: loading || showClearIcon === 'flex' ? 'flex' : 'none' }}>
            {loading ? (
              <CircularProgress color="secondary" size={18} />
            ) : (
              <ClearIcon onClick={handleClear} sx={{ cursor: 'pointer' }} />
            )}
          </InputAdornment>
        ),
      }}
    />
  );
};

const renderGlobalSearchLoadingState = (loadingMessage?: string) => (
  <Box sx={{ marginTop: '12px' }}>
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      sx={{ minHeight: '200px', gap: 2, px: 2 }}
    >
      <CircularProgress color="secondary" size={48} />
      {loadingMessage && (
        <Typography variant="body1" color="text.secondary" textAlign="center">
          {loadingMessage}
        </Typography>
      )}
    </Box>
  </Box>
);

type GlobalSearchResultsTableProps<T extends { _id: string }> = {
  results: T[];
  tableName: string;
  loadingMessage?: string;
  columns: {
    name?: string;
    title?: string;
    isVisible?: boolean;
    minWidth?: number;
    width?: number;
  }[];
  cellValuesMap: (item: T) => Record<string, { value?: React.ReactNode; customCell?: React.ReactNode }>;
  openRecord?: OpenRecordConfig<T>;
};

const GlobalSearchResultsTable = <T extends { _id: string }>({
  results,
  tableName,
  loadingMessage,
  columns,
  cellValuesMap,
  openRecord,
}: GlobalSearchResultsTableProps<T>) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(false);
    if (results.length === 0) {
      return;
    }

    // DataGrid syncs localList in useEffect; mount it hidden, then reveal after sync.
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setReady(true));
    });
    return () => cancelAnimationFrame(frame);
  }, [results]);

  return (
    <>
      {!ready && renderGlobalSearchLoadingState(loadingMessage)}
      <Box sx={{ mt: 1.5, width: '100%', display: ready ? 'block' : 'none' }}>
        <DataGrid
          name={tableName}
          endpoint="__global_search_local__"
          localList={results}
          hideSearchInput
          hidePagination
          hideColumnsManager
          edit={{ disable: true }}
          columns={columns}
          cellValuesMap={item => cellValuesMap(item)}
          openRecord={openRecord as OpenRecordConfig<Item> | undefined}
          sx={globalSearchResultsTableSx}
        />
      </Box>
    </>
  );
};

const renderWhyMatch = (whyMatch?: WhyMatch, matchedContact?: MatchedContact) => {
  if (!whyMatch?.reasons?.length) {
    return null;
  }

  const tooltipParts = whyMatch.reasons.map(r => (r.detail ? `${r.label}: ${r.detail}` : r.label)).join('\n');

  return (
    <Tooltip title={tooltipParts || whyMatch.summary}>
      <Box>
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, whiteSpace: 'nowrap' }}>
          {whyMatch.reasons.map(reason => (
            <Chip key={reason.field} label={reason.label} size="small" variant="outlined" />
          ))}
        </Box>
        {matchedContact && (
          <Typography
            variant="caption"
            sx={{ display: 'block', marginTop: '4px', color: 'warning.main', whiteSpace: 'nowrap' }}
          >
            Contact: {`${matchedContact.firstName || ''} ${matchedContact.lastName || ''}`.trim()}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const GlobalSearch = () => {
  const router = useRouter();
  const routerQuery = router.query;

  const [candidatesQuery, setCandidatesQuery] = useState('');
  const [clientsQuery, setClientsQuery] = useState('');
  const [jobsQuery, setJobsQuery] = useState('');
  const [jobDescription, setJobDescription] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState(CANDIDATES_TAB);
  const [activeLoader, setActiveLoader] = useState<null | 'query' | 'resume' | 'job'>(null);

  const [candidates, setCandidates] = useState<CandidateSearchResult[]>([]);
  const [candidateJobsById, setCandidateJobsById] = useState<Record<string, CandidateJobSummary>>({});
  const [candidateJobsLoading, setCandidateJobsLoading] = useState(false);
  const candidateJobsRequestIdRef = useRef(0);
  const [clients, setClients] = useState<ClientSearchResult[]>([]);
  const [jobs, setJobs] = useState<JobSearchResult[]>([]);
  const [totalCounts, setTotalCounts] = useState({ candidates: 0, clients: 0, jobs: 0 });

  const [hasSearchedCandidates, setHasSearchedCandidates] = useState(false);
  const [hasSearchedClients, setHasSearchedClients] = useState(false);
  const [hasSearchedJobs, setHasSearchedJobs] = useState(false);
  const [candidatesSearchMode, setCandidatesSearchMode] = useState<CandidateInputMode | null>(null);
  const [candidateInputMode, setCandidateInputMode] = useState<CandidateInputMode>('query');
  const [resumeBusy, setResumeBusy] = useState(false);
  const resumeSearchPendingRef = useRef(false);

  const [loadingStates, setLoadingStates] = useState({
    candidates: false,
    clients: false,
    jobs: false,
  });

  const setLoading = useCallback((type: keyof typeof loadingStates, loading: boolean) => {
    setLoadingStates(prev => ({ ...prev, [type]: loading }));
  }, []);

  const setTotalCount = useCallback((type: keyof typeof totalCounts, count: number) => {
    setTotalCounts(prev => ({ ...prev, [type]: count }));
  }, []);

  const persistSearchQuery = useCallback(
    (searchQuery: string) => {
      const trimmed = searchQuery.trim();
      const current = typeof routerQuery.search === 'string' ? routerQuery.search : '';

      if (trimmed === current) {
        return;
      }

      const nextQuery = { ...routerQuery };

      if (trimmed) {
        nextQuery.search = trimmed;
      } else {
        delete nextQuery.search;
      }

      void router.replace({ pathname: router.pathname, query: nextQuery }, undefined, { shallow: true });
    },
    [router, routerQuery],
  );

  const createSearchFunction = useCallback(
    <T extends CandidateSearchResult | ClientSearchResult | JobSearchResult>(
      endpoint: string,
      setLoading: (loading: boolean) => void,
      setResults: (results: T[]) => void,
      resultKey: string,
      setTotal: (count: number) => void,
    ) => {
      return async (searchQuery: string, resumeUrl?: string, jobDesc?: string) => {
        setLoading(true);
        try {
          const response = await sendApiRequest<Record<string, unknown>>({
            path: endpoint,
            data: {
              query: searchQuery,
              resumeUrl,
              jobDescription: jobDesc,
            },
            method: 'POST',
          });

          const results = (response[resultKey] as T[] | undefined) || [];

          setResults(results);
          setTotal(typeof response.totalCount === 'number' ? response.totalCount : results.length);
        } catch (error) {
          notify(error);
          setResults([]);
          setTotal(0);
        } finally {
          setLoading(false);
        }
      };
    },
    [],
  );

  const searchCandidates = useMemo(
    () =>
      createSearchFunction(
        '/global-search/candidates',
        loading => setLoading('candidates', loading),
        setCandidates,
        'candidates',
        count => setTotalCount('candidates', count),
      ),
    [createSearchFunction, setLoading, setTotalCount],
  );

  const searchClients = useMemo(
    () =>
      createSearchFunction(
        '/global-search/clients',
        loading => setLoading('clients', loading),
        setClients,
        'clients',
        count => setTotalCount('clients', count),
      ),
    [createSearchFunction, setLoading, setTotalCount],
  );

  const searchJobs = useMemo(
    () =>
      createSearchFunction(
        '/global-search/jobs',
        loading => setLoading('jobs', loading),
        setJobs,
        'jobs',
        count => setTotalCount('jobs', count),
      ),
    [createSearchFunction, setLoading, setTotalCount],
  );

  const runCandidatesSearch = useCallback(
    async (q?: string, resumeUrl?: string, source?: 'query' | 'resume' | 'job') => {
      const safeQuery = (q ?? '').trim();
      setCandidatesQuery(safeQuery);

      const derivedSourceEarly: 'query' | 'resume' | 'job' =
        source ?? (resumeUrl ? 'resume' : jobDescription.trim() ? 'job' : 'query');

      if (derivedSourceEarly === 'query' && safeQuery.length > 0 && safeQuery.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) {
        return;
      }

      persistSearchQuery(safeQuery);

      const jobDesc =
        source === 'job' ? jobDescription.trim() : safeQuery || resumeUrl ? undefined : jobDescription.trim();

      const hasCriteria = Boolean(safeQuery || resumeUrl || jobDesc);
      setHasSearchedCandidates(hasCriteria);

      if (!hasCriteria) {
        setCandidates([]);
        setTotalCount('candidates', 0);
        setCandidatesSearchMode(null);
        return;
      }

      const derivedSource: 'query' | 'resume' | 'job' = source ?? (resumeUrl ? 'resume' : jobDesc ? 'job' : 'query');

      setCandidatesSearchMode(derivedSource);
      setCandidateInputMode(derivedSource);
      setActiveLoader(derivedSource);
      await searchCandidates(safeQuery, resumeUrl, jobDesc || undefined);
      setActiveLoader(null);
    },
    [jobDescription, persistSearchQuery, searchCandidates, setTotalCount],
  );

  const runClientsSearch = useCallback(
    async (q?: string) => {
      const safeQuery = (q ?? '').trim();
      setClientsQuery(safeQuery);

      if (safeQuery.length > 0 && safeQuery.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) {
        return;
      }

      persistSearchQuery(safeQuery);

      setHasSearchedClients(true);

      if (!safeQuery) {
        setClients([]);
        setTotalCount('clients', 0);
        return;
      }

      setActiveLoader('query');
      await searchClients(safeQuery);
      setActiveLoader(null);
    },
    [persistSearchQuery, searchClients, setTotalCount],
  );

  const runJobsSearch = useCallback(
    async (q?: string) => {
      const safeQuery = (q ?? '').trim();
      setJobsQuery(safeQuery);

      if (safeQuery.length > 0 && safeQuery.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) {
        return;
      }

      persistSearchQuery(safeQuery);

      setHasSearchedJobs(true);

      if (!safeQuery) {
        setJobs([]);
        setTotalCount('jobs', 0);
        return;
      }

      setActiveLoader('query');
      await searchJobs(safeQuery);
      setActiveLoader(null);
    },
    [persistSearchQuery, searchJobs, setTotalCount],
  );

  const { candidates: loadingCandidates, clients: loadingClients, jobs: loadingJobs } = loadingStates;

  useEffect(() => {
    if (!hasSearchedCandidates || loadingCandidates) {
      if (!loadingCandidates) {
        setCandidateJobsById({});
        setCandidateJobsLoading(false);
      }
      return;
    }

    if (candidates.length === 0) {
      setCandidateJobsById({});
      setCandidateJobsLoading(false);
      return;
    }

    const requestId = ++candidateJobsRequestIdRef.current;
    const candidateIds = candidates.map(candidate => candidate._id);

    setCandidateJobsLoading(true);
    setCandidateJobsById({});

    void sendApiRequest<{ candidateJobs: Record<string, CandidateJobSummary> }>({
      path: '/candidates/jobs-summary',
      method: 'POST',
      data: { candidateIds },
    })
      .then(response => {
        if (requestId !== candidateJobsRequestIdRef.current) {
          return;
        }

        setCandidateJobsById(response.candidateJobs || {});
      })
      .catch(error => {
        if (requestId === candidateJobsRequestIdRef.current) {
          notify(error);
        }
      })
      .finally(() => {
        if (requestId === candidateJobsRequestIdRef.current) {
          setCandidateJobsLoading(false);
        }
      });
  }, [candidates, hasSearchedCandidates, loadingCandidates]);

  const isResumeBusy = resumeBusy || (activeLoader === 'resume' && loadingCandidates);
  const isJobBusy = activeLoader === 'job' && loadingCandidates;

  const isCandidatesQuerySearchLoading =
    loadingCandidates && (candidatesSearchMode === 'query' || activeLoader === 'query');

  const whyMatchColumnFor = (tabQuery: string) =>
    tabQuery ? [{ name: 'whyMatch', title: 'Why matched', isVisible: true }] : [];

  const tabLabel = (name: string, loading: boolean, hasSearched: boolean, count: number) => {
    if (loading) {
      return `${name}...`;
    }
    if (!hasSearched) {
      return name;
    }
    return `${name} (${formatTotalCount(count)})`;
  };

  const renderTabs = () => {
    const tabItems = [
      {
        label: tabLabel('Candidates', loadingCandidates || resumeBusy, hasSearchedCandidates, totalCounts.candidates),
        value: CANDIDATES_TAB,
      },
      {
        label: tabLabel('Clients', loadingClients, hasSearchedClients, totalCounts.clients),
        value: CLIENTS_TAB,
      },
      {
        label: tabLabel('Jobs', loadingJobs, hasSearchedJobs, totalCounts.jobs),
        value: JOBS_TAB,
      },
    ];

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        {tabItems.map(tab => {
          const active = activeTab === tab.value;

          return (
            <Box
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
                p: '5px 10px',
                borderBottom: theme => `2px solid ${active ? theme.palette.text.primary : 'transparent'}`,
                color: active ? 'text.primary' : 'text.secondary',
              }}
            >
              {tab.label}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderCandidateInputTabs = () => {
    const modes: { label: string; value: CandidateInputMode }[] = [
      { label: 'Keywords', value: 'query' },
      { label: 'Resume upload', value: 'resume' },
      { label: 'Job description', value: 'job' },
    ];

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mt: 1.5,
          mb: 1.5,
        }}
      >
        {modes.map(mode => {
          const active = candidateInputMode === mode.value;

          return (
            <Button
              key={mode.value}
              size="small"
              variant="contained"
              color={active ? 'primary' : 'inherit'}
              onClick={() => setCandidateInputMode(mode.value)}
            >
              {mode.label}
            </Button>
          );
        })}
      </Box>
    );
  };

  const renderResumeUploadPanel = () => (
    <Group>
      <Box
        sx={{
          minHeight: JOB_DESCRIPTION_PANEL_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Upload a resume file to find similar candidates
        </Typography>

        {isResumeBusy &&
          renderCandidateSearchBusyAlert(
            resumeBusy && !loadingCandidates ? 'Uploading resume' : 'Analyzing resume',
            resumeBusy && !loadingCandidates
              ? 'Uploading your file. Please wait.'
              : 'Reading your resume and finding similar candidates. This may take a moment.',
          )}

        <Box
          sx={{
            opacity: isResumeBusy ? 0.5 : 1,
            pointerEvents: isResumeBusy ? 'none' : 'auto',
          }}
        >
          <Uploader
            label={isResumeBusy ? 'Please wait...' : 'Upload Resume'}
            showList={false}
            multiple={false}
            accept=".pdf,.doc,.docx"
            onStartUploading={() => {
              setActiveTab(CANDIDATES_TAB);
              setCandidateInputMode('resume');
              setResumeBusy(true);
            }}
            onFinishUploading={() => {
              if (!resumeSearchPendingRef.current) {
                setResumeBusy(false);
              }
            }}
            onChange={result => {
              if (Array.isArray(result) || !result.url) {
                setResumeBusy(false);
                return;
              }

              resumeSearchPendingRef.current = true;
              void runCandidatesSearch('', result.url, 'resume').finally(() => {
                resumeSearchPendingRef.current = false;
                setResumeBusy(false);
              });
            }}
          />
        </Box>

        <Typography variant="caption" sx={{ display: 'block', marginTop: '8px', color: 'text.secondary' }}>
          Supported formats: PDF, DOCX
        </Typography>
      </Box>
    </Group>
  );

  const renderJobDescriptionPanel = () => (
    <Group>
      <Box
        sx={{
          height: JOB_DESCRIPTION_PANEL_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Paste a job description to find matching candidates
        </Typography>

        {isJobBusy &&
          renderCandidateSearchBusyAlert(
            'Searching candidates',
            'Reading your job description and finding similar candidates. This may take a moment.',
          )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            opacity: isJobBusy ? 0.5 : 1,
            pointerEvents: isJobBusy ? 'none' : 'auto',
          }}
        >
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            placeholder="Paste job description or keywords"
            multiline
            value={jobDescription}
            onChange={e => setJobDescription(e.target.value)}
            disabled={isJobBusy}
            sx={{
              flex: 1,
              '& .MuiInputBase-root': {
                height: '100%',
                alignItems: 'flex-start',
              },
              '& textarea': {
                height: '100% !important',
                overflow: 'auto !important',
                boxSizing: 'border-box',
              },
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setActiveTab(CANDIDATES_TAB);
                setCandidateInputMode('job');
                void runCandidatesSearch(undefined, undefined, 'job');
              }}
              disabled={isJobBusy}
            >
              {isJobBusy ? 'Please wait...' : 'Search'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Group>
  );

  const renderTabContent = <T extends { _id: string }>(
    loading: boolean,
    hasSearched: boolean,
    results: T[],
    tableName: string,
    noResultsText: string,
    columns: {
      name?: string;
      title?: string;
      isVisible?: boolean;
      minWidth?: number;
      width?: number;
    }[],
    cellValuesMap: (item: T) => Record<string, { value?: React.ReactNode; customCell?: React.ReactNode }>,
    searchInProgress?: boolean,
    loadingMessage?: string,
    openRecord?: OpenRecordConfig<T>,
  ) => {
    if (!hasSearched) return null;

    const inProgress = searchInProgress ?? loading;

    if (loading) {
      return renderGlobalSearchLoadingState(loadingMessage);
    }

    if (results.length === 0) {
      if (inProgress) return null;

      return (
        <Typography variant="body2" color="text.secondary" sx={{ marginTop: '12px' }}>
          {noResultsText}
        </Typography>
      );
    }

    return (
      <GlobalSearchResultsTable
        results={results}
        tableName={tableName}
        loadingMessage={loadingMessage}
        columns={columns}
        cellValuesMap={cellValuesMap}
        openRecord={openRecord}
      />
    );
  };

  const previewResult = (type: 'candidate' | 'job' | 'client', itemId: string) => {
    void router.push({
      pathname: router.pathname,
      query: { ...routerQuery, gsPreviewType: type, gsPreviewId: itemId },
    });
  };

  const onClosePreview = () => {
    void router.push({
      pathname: router.pathname,
      query: { ...routerQuery, gsPreviewType: null, gsPreviewId: null },
    });
  };

  const renderCandidatesPreview = () => {
    const { gsPreviewType, gsPreviewId } = routerQuery;

    if (gsPreviewType !== 'candidate' || !gsPreviewId) {
      return null;
    }

    return (
      <CandidatePreview
        queryParamName="gsPreviewId"
        tableTotalCount={candidates.length}
        tableList={candidates}
        onDelete={onClosePreview}
        onLoad={() => {}}
        onClose={onClosePreview}
      />
    );
  };

  const renderJobsPreview = () => {
    const { gsPreviewType, gsPreviewId } = routerQuery;

    if (gsPreviewType !== 'job' || !gsPreviewId) {
      return null;
    }

    return (
      <JobPreview
        key={`jobs-preview-${gsPreviewId as string}`}
        queryParamName="gsPreviewId"
        onDelete={onClosePreview}
        onClose={onClosePreview}
      />
    );
  };

  const renderClientsPreview = () => {
    const { gsPreviewType, gsPreviewId } = routerQuery;

    if (gsPreviewType !== 'client' || !gsPreviewId) {
      return null;
    }

    return (
      <ClientPreview
        key={`clients-preview-${gsPreviewId as string}`}
        queryParamName="gsPreviewId"
        onClose={onClosePreview}
      />
    );
  };

  const renderModal = () => (
    <Modal
      open={showModal}
      onClose={() => {
        setShowModal(false);
        onClosePreview();
      }}
      {...commonRightModalProps}
      size="lg"
      sx={{
        width: {
          xs: '100%',
          md: '70%',
        },
        left: 'inherit',
      }}
      preferencesTableName="global-search-modal"
      title="Search"
      renderContent={() => (
        <Box>
          {renderTabs()}

          <CustomTabPanel value={activeTab} index={CANDIDATES_TAB} style={globalSearchTabPanelSx} keepMounted>
            {renderCandidateInputTabs()}
            {candidateInputMode === 'query' && (
              <>
                {renderBooleanSearchInfo()}
                <GlobalSearchTabInput
                  value={candidatesQuery}
                  loading={isCandidatesQuerySearchLoading}
                  onSearch={value => void runCandidatesSearch(value, undefined, 'query')}
                  onClear={() => void runCandidatesSearch('', undefined, 'query')}
                />
              </>
            )}
            {candidateInputMode === 'resume' && renderResumeUploadPanel()}
            {candidateInputMode === 'job' && renderJobDescriptionPanel()}
            {renderTabContent(
              loadingCandidates,
              hasSearchedCandidates,
              candidates,
              'global-search-candidates',
              'No candidates found',
              [
                { name: 'name', title: 'Name', isVisible: true },
                ...whyMatchColumnFor(candidatesQuery),
                { name: 'email', title: 'Email', isVisible: true },
                { name: 'recentJob', title: 'Recent Job', isVisible: true },
                { name: 'allJobs', title: 'All Jobs', isVisible: true },
              ],
              (item: CandidateSearchResult) => ({
                email: { value: item.primaryEmail },
                recentJob: {
                  value: candidateJobsLoading
                    ? renderCandidateJobCellPlaceholder()
                    : (() => {
                        const recentJob = candidateJobsById[item._id]?.recentJob;

                        if (!recentJob) {
                          return null;
                        }

                        return (
                          <Link href={`/job?id=${recentJob._id}`} target="_blank" sx={{ cursor: 'pointer' }}>
                            {recentJob.title}
                          </Link>
                        );
                      })(),
                },
                allJobs: {
                  value: <GlobalSearchCandidateAllJobs candidate={item} />,
                },
                ...(candidatesQuery ? { whyMatch: { value: renderWhyMatch(item.whyMatch), doNotTruncate: true } } : {}),
              }),
              loadingCandidates,
              loadingCandidates ? 'Searching candidates...' : undefined,
              {
                columnName: 'name',
                getLabel: (item: CandidateSearchResult) => (
                  <Box display="flex" alignItems="center">
                    {item.avatarUrl ? (
                      <Avatar
                        src={readFile(item.avatarUrl)}
                        sx={{ width: '20px', height: '20px', marginRight: '5px' }}
                      />
                    ) : (
                      <PersonIcon sx={{ width: '20px', height: '20px', marginRight: '5px' }} />
                    )}
                    {`${item.firstName || ''} ${item.lastName || ''}`}
                  </Box>
                ),
                getHref: (item: CandidateSearchResult) => `/candidate?id=${item._id}`,
                onPreview: (item: CandidateSearchResult) => previewResult('candidate', item._id),
              },
            )}
          </CustomTabPanel>

          <CustomTabPanel value={activeTab} index={CLIENTS_TAB} style={globalSearchTabPanelSx} keepMounted>
            <GlobalSearchTabInput
              value={clientsQuery}
              loading={loadingClients}
              onSearch={value => void runClientsSearch(value)}
              onClear={() => void runClientsSearch('')}
            />
            {renderTabContent(
              loadingClients,
              hasSearchedClients,
              clients,
              'global-search-clients',
              'No clients found',
              [
                { name: 'name', title: 'Name', isVisible: true },
                ...whyMatchColumnFor(clientsQuery),
                { name: 'email', title: 'Email', isVisible: true },
                { name: 'details', title: 'Details', isVisible: true },
              ],
              (item: ClientSearchResult) => ({
                email: { value: item.companyEmailAddress },
                details: {
                  value: (() => {
                    if (item.contacts && item.contacts.length > 0 && clientsQuery) {
                      const lowerSearch = clientsQuery.toLowerCase();
                      const contact = item.contacts.find(contact => {
                        const firstName = (contact.firstName || '').toLowerCase();
                        const lastName = (contact.lastName || '').toLowerCase();
                        const title = (contact.title || '').toLowerCase();
                        const fullName = `${firstName} ${lastName}`.toLowerCase();

                        return (
                          (firstName || '').includes(lowerSearch) ||
                          (lastName || '').includes(lowerSearch) ||
                          (title || '').includes(lowerSearch) ||
                          (contact.primaryEmail || '').includes(lowerSearch) ||
                          (contact.emails?.find(email => email.email.includes(lowerSearch))?.email || '').includes(
                            lowerSearch,
                          ) ||
                          fullName.includes(lowerSearch)
                        );
                      });

                      if (contact) {
                        return (
                          <Box sx={{ fontSize: '0.85rem' }}>
                            <Box component="span" sx={{ fontWeight: 'medium' }}>
                              Found contact:{' '}
                            </Box>
                            <Box sx={{ color: 'warning.main' }} component="span">{`${contact.firstName || ''} ${
                              contact.lastName || ''
                            }`}</Box>
                          </Box>
                        );
                      }
                    }
                    return item.address ? `${item.address.state || ''}, ${item.address.city || ''}` : '';
                  })(),
                },
                ...(clientsQuery
                  ? { whyMatch: { value: renderWhyMatch(item.whyMatch, item.matchedContact), doNotTruncate: true } }
                  : {}),
              }),
              loadingClients,
              loadingClients ? 'Searching clients...' : undefined,
              {
                columnName: 'name',
                getLabel: (item: ClientSearchResult) => (
                  <Box display="flex" alignItems="center">
                    {item.logo ? (
                      <Avatar src={readFile(item.logo)} sx={{ width: '20px', height: '20px', marginRight: '5px' }} />
                    ) : (
                      <BusinessIcon sx={{ width: '20px', height: '20px', marginRight: '5px' }} />
                    )}
                    {item.name}
                  </Box>
                ),
                getHref: (item: ClientSearchResult) => `/client?id=${item._id}`,
                onPreview: (item: ClientSearchResult) => previewResult('client', item._id),
              },
            )}
          </CustomTabPanel>

          <CustomTabPanel value={activeTab} index={JOBS_TAB} style={globalSearchTabPanelSx} keepMounted>
            <GlobalSearchTabInput
              value={jobsQuery}
              loading={loadingJobs}
              onSearch={value => void runJobsSearch(value)}
              onClear={() => void runJobsSearch('')}
            />
            {renderTabContent(
              loadingJobs,
              hasSearchedJobs,
              jobs,
              'global-search-jobs',
              'No jobs found',
              [
                { name: 'title', title: 'Title', isVisible: true },
                ...whyMatchColumnFor(jobsQuery),
                { name: 'clientName', title: 'Client', isVisible: true },
                { name: 'location', title: 'Location', isVisible: true },
              ],
              (item: JobSearchResult) => ({
                clientName: { value: item.clientName },
                location: { value: item.location },
                ...(jobsQuery ? { whyMatch: { value: renderWhyMatch(item.whyMatch), doNotTruncate: true } } : {}),
              }),
              loadingJobs,
              loadingJobs ? 'Searching jobs...' : undefined,
              {
                columnName: 'title',
                getLabel: (item: JobSearchResult) => item.title || '—',
                getHref: (item: JobSearchResult) => `/job?id=${item._id}`,
                onPreview: (item: JobSearchResult) => previewResult('job', item._id),
              },
            )}
          </CustomTabPanel>
        </Box>
      )}
    />
  );

  return (
    <>
      {renderModal()}
      {renderCandidatesPreview()}
      {renderJobsPreview()}
      {renderClientsPreview()}

      <SearchIcon
        onClick={() => setShowModal(true)}
        sx={{
          cursor: 'pointer',
          width: '28px',
          height: '28px',
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          borderRadius: '50%',
          padding: '6px',
        }}
      />
    </>
  );
};

export default GlobalSearch;
